import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useAccount, useContractRead, useContractWrite, usePrepareContractWrite, useProvider, useWaitForTransaction } from 'wagmi'
import contractInterface from '../../mintingABI.json'
import { parse } from 'papaparse'
import { useForm } from 'react-hook-form'
import { ethers } from 'ethers'
import { IsJsonString } from '../../utils'
import FlipCard, { BackCard, FrontCard } from './flip-card.component'
import { Link } from 'react-router-dom'
import { SnackbarProvider, enqueueSnackbar } from 'notistack'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid, regular, brands } from '@fortawesome/fontawesome-svg-core/import.macro'
import CarsouselComponent from '../carousel/carousel.component'

const MINT_PRICE_ETH = '25'
const CONTRACT_ADDRESS = '0x765875caD9821A9c3E861d16342Da76934d74744'

const TELOS_MAINNET_PROVIDER = new ethers.providers.JsonRpcProvider("https://mainnet.telos.net/evm");;


export enum MintState {
  PUBLIC = 0,
  WHITELIST = 1,
  CLOSED = 2
}

export interface WhitelistData {
  wallet: string
  whitelistQuantity: string
  spotInWhitelist: string
  proof: string
}

export interface WalletData {
  connected: boolean
  walletAddress: string
  chainId: number
}

const contractConfig = {
  addressOrName: CONTRACT_ADDRESS,
  contractInterface: contractInterface,
}

const RainbowKitMint = memo(() => {
  const [totalMintSupply, setTotalMintSupply] = React.useState(0)
  const [maxSupply, setMaxSupply] = React.useState(0)
  const [totalMinted, setTotalMinted] = React.useState(0)
  const [mintState, setMintState] = React.useState(-1);
  const { isConnected, address } = useAccount()

  const [walletData, setWalletData] = useState<WalletData>()
  const [numberToMint, setNumberToMint] = useState<number>(1)

  const [status, setStatus] = useState<string>('')
  const [statusType, setStatusType] = useState<string>('')

  /* Get maxSupply value from contract (max tokens that can be minted) */
  const { data: maxSupplyData } = useContractRead({
    ...contractConfig,
    functionName: 'MAX_SUPPLY',
    watch: true,
  })

  /* Set maxSupply state value */
  useEffect(() => {
    if (maxSupplyData) {
      setMaxSupply(maxSupplyData.toNumber())
    }
  }, [maxSupplyData])

  const { data: totalMintsFromWallet } = useContractRead({
    ...contractConfig,
    functionName: 'balanceOf',
    watch: true,
    args: [walletData?.walletAddress],
  })
  /* Set total NFTs Minted by this wallet */
  useEffect(() => {
    if (totalMintsFromWallet) {
      setTotalMinted(totalMintsFromWallet.toNumber())
    }
  }, [totalMintsFromWallet])

  /* Set the users wallet data state object */
  useEffect(() => {
    setWalletData({
      connected: true,
      walletAddress: `${address}`,
      chainId: 40,
    })
  }, [address])

  /* Get totalSupply value (total nft tokens minted so far) */
  const { data: totalSupplyData } = useContractRead({
    ...contractConfig,
    functionName: 'totalSupply',
    watch: true,
  })



  useEffect(() => {
    let slot = ethers.utils.hexZeroPad(ethers.utils.hexlify(10), 32)

    TELOS_MAINNET_PROVIDER.getStorageAt(CONTRACT_ADDRESS,  slot).then( (val) => {
      setMintState(ethers.BigNumber.from(val).toNumber())
    });
  } );

  /* Set totalSupply of NFTs minted so far for this contract */
  useEffect(() => {
    if (totalSupplyData) {
      setTotalMintSupply(totalSupplyData.toNumber())
    }
  }, [totalSupplyData])

  /* Set the users wallet data state object */
  useEffect(() => {
    setWalletData({
      connected: isConnected,
      walletAddress: `${address}`,
      chainId: 40,
    })
  }, [address])

  /* Setup mint function ready to be fired off when buttton is pushed */
  const { config: contractWriteConfig, error: prepareContractError } = usePrepareContractWrite({
    ...contractConfig,
    functionName: 'mint',
    args: [numberToMint],
    overrides: {
      value: ethers.utils.parseEther(MINT_PRICE_ETH).mul(numberToMint),

    },
  })

  /* Setup mint function ready to be fired off when buttton is pushed */
  const {
    data: mintData,
    write: mint,
    isLoading: isMintLoading,
    isSuccess: isMintStarted,
    error: mintError,
  } = useContractWrite(contractWriteConfig)

  /* Setup mint transaction response data */
  const {
    data: txData,
    isSuccess: txSuccess,
    error: txError,
  } = useWaitForTransaction({
    hash: mintData?.hash,
  })

  /* onClick event for form button, initiates transaction approval window */
  const whitelistMint = async () => {
    try {
      if (!address) {
        enqueueSnackbar('Error, you must connect to a wallet first.', { variant: 'error' })
        return
      }
      setStatus(`Minting ${numberToMint} NFT(s)...`)
      const mintVal = mint?.()
    } catch (error: any) {
      enqueueSnackbar(error?.reason, { variant: 'error' })
    }
  }

  /* Handle any errors that we might receive from the contract when preparing the write function */
  useEffect(() => {
    if (prepareContractError?.message) {
      const msg = prepareContractError?.message.substring(prepareContractError?.message.indexOf('error={"code"') + 6).split(', code=')[0]
      if (IsJsonString(msg)) {
        const transactionResponse = JSON.parse(msg)
        const err = transactionResponse?.message.replace('execution reverted:', 'Error:')
        setStatus(err)
        enqueueSnackbar(err, { variant: 'error' })
        setStatusType('error')
      }
    }
  }, [prepareContractError])

  /* Update status message when txSuccess boolean changes */
  useEffect(() => {
    if (txSuccess) {
      setStatus('Mint NFT')
      enqueueSnackbar('Your NFT(s) have been successfully minted.', { variant: 'success' })
      setStatusType('success')
    }
  }, [txSuccess])

  /* Show snackbar error & update statusMessage & type states if/when txError changes. */
  useEffect(() => {
    if (txError?.message) {
      setStatus(txError.message)
      setStatusType('error')
      enqueueSnackbar(txError.message, { variant: 'error' })
    }
  }, [txError])

  /* Show snackbar error & update statusMessage & type states if/when mintError changes. */
  useEffect(() => {
    if (mintError?.message) {
      setStatus(mintError.message)
      setStatusType('error')
      enqueueSnackbar(mintError.message, { variant: 'error' })
    }
  }, [mintError])

  const minimumMintsAllowed = 1
  const maximumMintsAllowed = 10

  const { handleSubmit } = useForm()

  return (
    <div className="page flex justify-center">
      <form onSubmit={handleSubmit(whitelistMint)}>
        <div className="">
          <p className="text-2xl text-white font-bold mb-2">
          <span
                className=" mt-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-emerald-400 to-green-500 md:inline-block"
              >   <FontAwesomeIcon icon={solid('cloud')} /> FluffDistrict<span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-500 via-cyon-400 to-purple-300"> NFT Mint!</span> </span>
            <small>{' '}
              ({totalMintSupply}/{maxSupply} Minted)
            </small>
          </p>
          <img src="Cubby Hole(2).png" alt="Fluff Clouds" className="rounded-lg h-32 mb-2"/>

          <div className="flex-row md:flex">
            <div className="flex-col mr-4">
              <FlipCard>
                <FrontCard isCardFlipped={txSuccess}>
                  <CarsouselComponent />
                </FrontCard>
                <BackCard isCardFlipped={txSuccess}>
                  <div style={{ padding: 24, textAlign: 'center' }}>
                    <CarsouselComponent />

                    <p style={{ marginBottom: 6 }}>
                      View on <a href={`https://testnet.teloscan.io/tx/${mintData?.hash}`}>Teloscan</a>
                    </p>
                    <p>
                      View on <a href={`https://byt.io/telos/collection/fluffdistrict`}>Byt.io</a>
                    </p>
                  </div>
                </BackCard>
              </FlipCard>
            </div>

            <div className="flex-col w-1/2">
              <label htmlFor="name" className="mb-3 block text-base font-medium text-white">
                NFTs to Mint: <small>(Max of 10 NFTs Each Tx)</small>
              </label>
              <input
                type="number"
                value={numberToMint}
                min={minimumMintsAllowed}
                max={maximumMintsAllowed}
                onChange={(event) => setNumberToMint(Number(event?.target.value))}
                id="number"
                placeholder="Number of NFTs to Mint"
                className="w-full rounded-md border border-[#e0e0e0] bg-white py-3 px-6 text-base font-medium text-black outline-none focus:border-[#6A64F1] focus:shadow-md mintNumberInput"
              />
              <div className="text-white">
                {totalMinted > 0 && <p style={{ margin: '12px 0 5px', color: 'green' }}>You have minted {totalMinted} NFTs.</p>}

                {status && <p style={{ color: statusType == 'error' ? 'red' : 'green' }}>{status}</p>}

                {mintError && <p style={{ marginTop: 24, color: '#FF6257' }}>Error: {mintError.message}</p>}
                {txError && <p style={{ marginTop: 24, color: '#FF6257' }}>Error: {txError.message}</p>}

                <button
                  style={{ marginTop: 5 }}
                  className="hover:shadow-form rounded-md bg-[#6A64F1] py-3 px-8 text-base font-semibold text-white outline-none"
                  data-mint-loading={isMintLoading}
                  data-mint-started={isMintStarted}
                  type="submit"
                >
                  {status && statusType != 'error' && status}
                  {isMintLoading && 'Waiting for approval'}
                  {!isMintLoading && !isMintStarted && 'Mint NFT!'}
                </button>
              </div>
            </div>
          </div>

        </div>
      </form>
    </div>
  )
})

export default RainbowKitMint
